import React from 'react'
import parse from 'html-react-parser'
import { Button } from '~/components/_atoms'
import useTranslation from '../../../shared/TranslationProvider'
import { useCandidateChat } from '../../candidate/CandidateChatContext'
import candidateChatManager from '../../candidate/CandidateChatManager'
import { getEffectiveExitUrl } from '../../candidate/utils/getEffectiveExitUrl'
import { buildCoachListLabels } from '../shared/buildCoachListLabels'
import CoachListSections from '../shared/CoachListSections'
import {
  getAvailableCoachesHeading,
  getCoachJobTitle,
} from '../shared/getCoachJobTitle'
import { useExpandedCoaches } from '../shared/useExpandedCoaches'
import { useCoachListConnection } from './useCoachListConnection'

/**
 * Candidate coach picker on the video chat frame (discovery / after cancelled call).
 * Card UI/labels match the floating coach chat modal (CoachChatList).
 */
const CoachSelectionList = () => {
  const { t } = useTranslation('coach_chat')
  const candidateChat = useCandidateChat()
  const {
    inHours,
    deloitteUser,
    userCareerDevelopment,
    modalCopy,
    afterChatPath,
    getDarkModeClasses,
    conversationId,
  } = candidateChat

  const { toggleCoachExpanded, isCoachExpanded } = useExpandedCoaches()
  const labels = buildCoachListLabels(t)
  const { isConnected, coaches, isInitialized, availableCoaches, busyCoaches } =
    useCoachListConnection(candidateChat)

  const mediaAvailable = !(
    candidateChatManager.mediaService &&
    candidateChatManager.mediaService.isDegradedMode()
  )

  const handleCoachSelection = (coach, selectedChatMode) => {
    // Soft handoff: keep Starting Chat visible across reload into chat view
    candidateChatManager.beginChatHandoff(conversationId)

    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.set('coach', coach.easyrtcid)
    currentUrl.searchParams.set('chat_mode', selectedChatMode)

    // Brief delay so Starting Chat / startingChatAt can propagate before navigation
    setTimeout(() => {
      window.location.href = currentUrl.toString()
    }, 75)
  }

  const urlParams = new URLSearchParams(window.location.search)
  const isChatEnded = urlParams.get('chat_ended') === '1'
  const isCallMissed = urlParams.get('call_missed') === '1'

  if (!isInitialized || isChatEnded || isCallMissed) {
    return null
  }

  const handleExit = () => {
    window.top.location.href = getEffectiveExitUrl(afterChatPath)
  }

  const exitButton = (
    <Button
      variant="outline"
      color="secondary"
      size="md"
      fullWidth
      onClick={handleExit}
      className="h-10 rounded-2xl px-6"
    >
      {t('call_cancelled.exit', { defaultValue: 'Exit' })}
    </Button>
  )

  if (!isConnected) {
    return (
      <div className="coach-selection-container">
        <div className="tw-hidden py-8 text-center">
          <h2 className="text-lg font-medium text-intoo-green-medium-800">
            {t('CoachChatList.finding_coaches')}
          </h2>
          <p className="mt-2 text-sm text-intoo-green-medium-600">
            {t('coach_selection.finding_coaches_description')}
          </p>
        </div>
        <div className={`p-6 ${getDarkModeClasses('bg-white', 'bg-gray-800')}`}>
          {exitButton}
        </div>
      </div>
    )
  }

  if (coaches === null) {
    return (
      <div className="coach-selection-container">
        <div className="py-8 text-center">
          <h2 className="text-lg font-medium text-intoo-green-medium-800">
            {t('CoachChatList.loading_coaches')}
          </h2>
          <p className="mt-2 text-sm text-intoo-green-medium-600">
            {t('coach_selection.loading_coaches_description')}
          </p>
        </div>
      </div>
    )
  }

  const emptyMessageHtml = inHours ? modalCopy?.busy : modalCopy?.after_hours

  return (
    <div className="coach-selection-container container flex h-full flex-col">
      <div className="tw-hidden">
        <div className="mb-6 text-center">
          <h1 className="text-lg font-semibold text-intoo-green-medium-900 @md:text-xl @lg:text-2xl">
            {t('coach_selection.choose_coach_title')}
          </h1>
          <p className="mt-2 text-sm text-intoo-green-medium-700 @md:text-base">
            {t('coach_selection.choose_coach_description')}
          </p>
        </div>

        {coaches.length > 0 ? (
          <CoachListSections
            availableCoaches={availableCoaches}
            busyCoaches={busyCoaches}
            getJobTitle={(coach) =>
              getCoachJobTitle(coach, { t, deloitteUser })
            }
            getBusyMessage={() => labels.currentlyBusy}
            isCoachExpanded={isCoachExpanded}
            onToggleExpanded={toggleCoachExpanded}
            onSelect={handleCoachSelection}
            userCareerDevelopment={userCareerDevelopment}
            mediaAvailable={mediaAvailable}
            actionLabels={{
              video: labels.video,
              text: labels.text,
              audio: labels.audio,
            }}
            moreInfoLabel={labels.moreInfo}
            showLessLabel={labels.showLess}
            expandedLabels={{
              yearsExperience: labels.yearsExperience,
              supportedClients: labels.supportedClients,
              satisfactionRating: labels.satisfactionRating,
              aboutCoach: labels.aboutCoach,
            }}
            availableHeading={getAvailableCoachesHeading(t, { deloitteUser })}
            busyHeading={t('CoachChatList.busy_coaches', {
              defaultValue: 'Currently Busy',
            })}
            emptyMessageHtml={emptyMessageHtml}
          />
        ) : (
          <div className="py-8 text-center">
            <p className="text-lg text-intoo-green-medium-600">
              {emptyMessageHtml && parse(emptyMessageHtml)}
            </p>
          </div>
        )}
      </div>

      <div
        className={`mt-auto border-t p-6 ${getDarkModeClasses('border-cod-gray-200 bg-white', 'border-gray-600 bg-gray-800')}`}
      >
        {exitButton}
      </div>
    </div>
  )
}

export default CoachSelectionList
